import type { ReactNode } from 'react';

import { Markdown, markdownClasses } from '@/modules/assistant';

/**
 * One agent turn while its answer is being revealed.
 *
 * The full answer arrives in one POST from the assistant API, so this simulates
 * streaming on the client: an empty agent bubble appears first, the text reveals
 * chunk by chunk with a blinking caret at the end, and when the reveal finishes
 * the caret disappears and the full Markdown answer stays on screen.
 *
 * `Markdown` parses the revealed text on every render. A partial reveal may cut
 * off mid-syntax (an unclosed fence, an unterminated emphasis), but the final
 * complete text always renders correctly, which is what the person reads.
 */
export type StreamingMessageProps = {
  /** The complete answer to reveal. */
  text: string;
  /** Whether the reveal is still running. */
  isStreaming: boolean;
  /** Accessible name announced as each chunk arrives. */
  ariaLabel?: string;
  /** Class names added to the wrapper. */
  className?: string;
};

export function StreamingMessage({
  text,
  isStreaming,
  ariaLabel = 'Assistant answer',
  className,
}: StreamingMessageProps): ReactNode {
  return (
    <div className={className} aria-live="polite" aria-label={ariaLabel}>
      <div className={markdownClasses}>
        <Markdown text={text} />
        {isStreaming ? (
          <span
            className="caret"
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: '0.75rem',
              height: '1rem',
              background: 'currentColor',
              marginLeft: '0.125rem',
              verticalAlign: 'middle',
              animation: 'blink 1s infinite',
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
